import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, Button, Textarea, cn } from "@tellescope/ui";
import { AlertCircle, CheckCircle2, Copy, Check } from "lucide-react";
import type { PortalSchema } from "../../../shared/schema";
import { parseAndValidatePortalSchema } from "../../../shared/schema/validation";

interface JsonSchemaEditorProps {
  schema: PortalSchema;
  onApply: (json: unknown) => { success: boolean; errors?: string[] };
  className?: string;
}

interface ValidationState {
  isValid: boolean;
  errors: string[];
  jsonParseError: boolean;
}

/**
 * JSON editor for portal schema with two-layer validation:
 * 1. JSON syntax validation
 * 2. Zod schema validation
 */
export function JsonSchemaEditor({ schema, onApply, className }: JsonSchemaEditorProps) {
  const { t } = useTranslation();

  // Serialize schema to JSON for initial value
  const initialJson = useMemo(() => JSON.stringify(schema, null, 2), [schema]);

  const [jsonText, setJsonText] = useState(initialJson);
  const [validation, setValidation] = useState<ValidationState>({
    isValid: true,
    errors: [],
    jsonParseError: false,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [copied, setCopied] = useState(false);

  // Update JSON text when schema changes externally
  useEffect(() => {
    const newJson = JSON.stringify(schema, null, 2);
    setJsonText(newJson);
    setHasChanges(false);
    setValidation({ isValid: true, errors: [], jsonParseError: false });
  }, [schema]);

  // Validate JSON on change
  const handleChange = useCallback((value: string) => {
    setJsonText(value);
    setHasChanges(true);

    // Two-layer validation
    const result = parseAndValidatePortalSchema(value);

    if (result.success) {
      setValidation({ isValid: true, errors: [], jsonParseError: false });
    } else {
      const isJsonError = result.errors?.some((e) => e.includes("Invalid JSON"));
      setValidation({
        isValid: false,
        errors: result.errors ?? [],
        jsonParseError: isJsonError ?? false,
      });
    }
  }, []);

  // Apply valid JSON to schema
  const handleApply = useCallback(() => {
    if (!validation.isValid) return;

    try {
      const data = JSON.parse(jsonText);
      const result = onApply(data);

      if (result.success) {
        setHasChanges(false);
      } else {
        setValidation({
          isValid: false,
          errors: result.errors ?? [t("Failed to apply schema")],
          jsonParseError: false,
        });
      }
    } catch {
      setValidation({
        isValid: false,
        errors: [t("Invalid JSON format")],
        jsonParseError: true,
      });
    }
  }, [jsonText, validation.isValid, onApply, t]);

  // Copy JSON to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  }, [jsonText]);

  // Reset to current schema
  const handleReset = useCallback(() => {
    const newJson = JSON.stringify(schema, null, 2);
    setJsonText(newJson);
    setHasChanges(false);
    setValidation({ isValid: true, errors: [], jsonParseError: false });
  }, [schema]);

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header with status and actions */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          {validation.isValid ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <AlertCircle className="h-4 w-4 text-destructive" />
          )}
          <span className="text-sm font-medium">
            {validation.isValid ? t("Valid JSON Schema") : t("Invalid Schema")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 px-2">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            <span className="ml-1 text-xs">{copied ? t("Copied") : t("Copy")}</span>
          </Button>
          {hasChanges && (
            <>
              <Button variant="ghost" size="sm" onClick={handleReset} className="h-7 px-2 text-xs">
                {t("Reset")}
              </Button>
              <Button size="sm" onClick={handleApply} disabled={!validation.isValid} className="h-7 px-3 text-xs">
                {t("Apply Changes")}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Validation errors */}
      {!validation.isValid && validation.errors.length > 0 && (
        <Alert variant="destructive" className="m-4 mb-0">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              {validation.jsonParseError ? (
                <p className="font-medium">{t("JSON Syntax Error")}</p>
              ) : (
                <p className="font-medium">{t("Schema Validation Errors")}</p>
              )}
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {validation.errors.map((error, i) => (
                  <li key={i} className="break-all">
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* JSON editor */}
      <div className="flex-1 min-h-0 p-4">
        <Textarea
          value={jsonText}
          onChange={(e) => handleChange(e.target.value)}
          className={cn(
            "h-full min-h-[400px] font-mono text-sm resize-none",
            "bg-slate-950 text-slate-100 border-slate-700",
            !validation.isValid && "border-destructive ring-destructive/20 ring-2"
          )}
          spellCheck={false}
          aria-invalid={!validation.isValid}
        />
      </div>
    </div>
  );
}
